import React, { useState, useEffect } from "react";
import {
  FaUsers,
  FaClipboardCheck,
  FaBuilding,
  FaBullhorn,
  FaEye,
  FaArrowRight,
  FaClock,
  FaCheckCircle,
  FaExclamationTriangle,
  FaChartBar,
  FaChartPie,
  FaRocket,
  FaUserCircle,
  FaCalendarAlt,
  FaChartLine,
  FaFilter,
  FaDownload,
  FaSearch,
} from "react-icons/fa";
import { Link } from "react-router-dom";
import { useAllUsers, useVerificationRequests } from "../../hooks/useAdmin.ts";
import { useBrands } from "../../hooks/useBrands.ts";
import { useCampaigns } from "../../hooks/useCampaigns.ts";
import {
  BarChart,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ResponsiveContainer,
  PieChart,
  Pie,
  Cell,
  AreaChart,
  Area,
} from "recharts";
import { useTheme } from "../../contexts/ThemeContext.tsx";
import { getTheme } from "../../theme.ts";

// Define types
interface VerificationRequest {
  id?: string;
  organization_name?: string;
  influencer_name?: string;
  type?: 'influencer' | null;
  influencer?: {
    user?: {
      first_name?: string;
      last_name?: string;
      avatar?: string;
    };
  };
  organization?: {
    name?: string;
    logo?: string;
  };
  requested_at?: string;
  status?: string;
  influencer_id?: string;
  organization_id?: string;
}

interface Campaign {
  id?: string;
  name?: string;
  created_at?: string;
}

interface Stats {
  verifications: number;
  brands: number;
  campaings: number;
  users: number;
}

interface Activity {
  type: 'verification' | 'campaign' | 'default';
  message: string;
  time: string;
  status?: string;
  userType?: 'influencer' | 'organization';
  userId?: string;
}

interface ChartData {
  name: string;
  value: number;
}

interface TrendData {
  month: string;
  users: number;
  campaigns: number;
  brands: number;
}

interface StatCardProps {
  icon: React.ComponentType<{ className?: string }>;
  title: string;
  value: number;
  color: string;
  subtitle?: string;
  trend?: number;
  delay?: number;
}

interface QuickActionButtonProps {
  to: string;
  icon: React.ComponentType<{ className?: string }>;
  children: React.ReactNode;
  color: string;
  description?: string;
}

interface ActivityItemProps {
  activity: Activity;
}

const AdminDashboard: React.FC = () => {
  const { themeName } = useTheme();
  const currentTheme = getTheme(themeName);
  const [requests, setRequests] = useState<VerificationRequest[]>([]);
  const [stats, setStats] = useState<Stats>({
    verifications: 0,
    brands: 0,
    campaings: 0,
    users: 0
  });
  const [totalVerifications, setTotalVerifications] = useState<number>(0);
  const [recentActivities, setRecentActivities] = useState<Activity[]>([]);
  const [timeRange, setTimeRange] = useState<'week' | 'month' | 'year'>('month');

  // Data fetching using hooks
  const { data: usersData } = useAllUsers();
  const { data: verificationsData } = useVerificationRequests();
  const { data: brandsData } = useBrands();
  const { data: campaignsData } = useCampaigns();

  // Derived state from hooks data
  useEffect(() => {
    const verifications = Array.isArray(verificationsData)
      ? verificationsData
      : (verificationsData as any)?.data || [];

    const pendingVerifications = verifications.filter(
      (v: any) => v.status?.toLowerCase() === "pending"
    );

    setTotalVerifications(verifications.length);
    setRequests(pendingVerifications);

    const usersCount = Array.isArray(usersData)
      ? usersData.length
      : (usersData as any)?.data?.length || 0;

    const campaignsCount = Array.isArray(campaignsData)
      ? campaignsData.length
      : (campaignsData as any)?.length || 0;

    const brandsCount = Array.isArray(brandsData)
      ? brandsData.length
      : (brandsData as any)?.length || 0;

    setStats({
      verifications: pendingVerifications.length,
      users: usersCount,
      campaings: campaignsCount,
      brands: brandsCount
    });

    const activities: Activity[] = [];
    
    verifications.slice(0, 3).forEach((v: any) => {
      if (v.requested_at) {
        const userType = v.influencer_name || v.type === 'influencer' ? 'influencer' : 'organization';
        const userName = v.influencer_name || v.organization_name || 'Someone';
        const userId = v.influencer_id || v.organization_id || v.id;
        
        activities.push({
          type: 'verification',
          message: `${userName} requested verification`,
          time: v.requested_at,
          status: v.status,
          userType: userType,
          userId: userId
        });
      }
    });

    if (campaignsData) {
      const recentCampaigns = Array.isArray(campaignsData) 
        ? campaignsData.slice(0, 2) 
        : (campaignsData as any)?.data?.slice(0, 2) || [];
      
      recentCampaigns.forEach((c: any) => {
        if (c.created_at) {
          activities.push({
            type: 'campaign',
            message: `Campaign "${c.name || 'Untitled'}" was created`,
            time: c.created_at,
            status: 'success'
          });
        }
      });
    }

    const sortedActivities = activities
      .sort((a, b) => new Date(b.time).getTime() - new Date(a.time).getTime())
      .slice(0, 5);
    
    setRecentActivities(sortedActivities);

  }, [usersData, verificationsData, brandsData, campaignsData]);

  const barChartData: ChartData[] = [
    { name: 'Verifications', value: totalVerifications },
    { name: 'Users', value: stats.users },
    { name: 'Brands', value: stats.brands },
    { name: 'Campaigns', value: stats.campaings },
  ];

  const pieChartData: ChartData[] = barChartData;

  const COLORS: string[] = [
    currentTheme.primary,
    currentTheme.secondary,
    currentTheme.success,
    currentTheme.warning
  ];

  const trendData: TrendData[] = [
    { month: 'Jan', users: 40, campaigns: 24, brands: 18 },
    { month: 'Feb', users: 55, campaigns: 28, brands: 22 },
    { month: 'Mar', users: 65, campaigns: 32, brands: 28 },
    { month: 'Apr', users: 78, campaigns: 38, brands: 32 },
    { month: 'May', users: 90, campaigns: 42, brands: 35 },
    { month: 'Jun', users: stats.users, campaigns: stats.campaings, brands: stats.brands },
  ];

  const StatCard: React.FC<StatCardProps> = ({ icon: Icon, title, value, color, subtitle, trend }) => (
    <div 
      className="group relative rounded-2xl p-6 transition-all duration-300 hover:shadow-xl hover:-translate-y-1"
      style={{ 
        backgroundColor: currentTheme.surface,
        border: `1px solid ${currentTheme.border}`,
        boxShadow: '0 4px 20px rgba(0,0,0,0.02)'
      }}
    >
      {/* Gradient overlay on hover */}
      <div 
        className="absolute inset-0 rounded-2xl opacity-0 group-hover:opacity-100 transition-opacity duration-300"
        style={{ 
          background: `linear-gradient(135deg, ${color}05, transparent)`,
          pointerEvents: 'none'
        }}
      />
      
      <div className="relative">
        <div className="flex items-start justify-between mb-4">
          <div 
            className="p-3 rounded-xl transition-transform duration-300 group-hover:scale-110 group-hover:rotate-3"
            style={{ 
              backgroundColor: `${color}08`,
              boxShadow: `0 8px 16px -8px ${color}30`
            }}
          >
            <Icon className="w-5 h-5" style={{ color }} />
          </div>
          
          {trend !== undefined && (
            <div 
              className="flex items-center gap-1 px-2.5 py-1.5 rounded-lg text-xs font-medium"
              style={{ 
                backgroundColor: trend > 0 ? `${currentTheme.success}08` : `${currentTheme.error}08`,
                color: trend > 0 ? currentTheme.success : currentTheme.error,
                border: `1px solid ${trend > 0 ? currentTheme.success : currentTheme.error}15`
              }}
            >
              <FaChartLine className={`w-3 h-3 ${trend < 0 ? 'rotate-180' : ''}`} />
              <span>{trend > 0 ? '+' : ''}{trend}%</span>
            </div>
          )}
        </div>
        
        <h3 className="text-sm font-medium mb-1.5" style={{ color: `${currentTheme.text}70` }}>
          {title}
        </h3>
        
        <p className="text-3xl font-bold mb-2 tracking-tight" style={{ color: currentTheme.text }}>
          {value.toLocaleString()}
        </p>
        
        {subtitle && (
          <div className="flex items-center gap-1.5">
            <div className="w-1 h-1 rounded-full" style={{ backgroundColor: color }} />
            <p className="text-xs" style={{ color: `${currentTheme.text}50` }}>
              {subtitle}
            </p>
          </div>
        )}
      </div>
    </div>
  );

  const QuickActionButton: React.FC<QuickActionButtonProps> = ({ to, icon: Icon, children, color, description }) => (
    <Link
      to={to}
      className="group relative flex items-center gap-4 p-4 rounded-xl transition-all duration-300 hover:shadow-md hover:-translate-y-0.5"
      style={{ 
        backgroundColor: `${color}04`,
        border: `1px solid ${color}12`,
      }}
    >
      <div 
        className="p-2.5 rounded-lg transition-all duration-300 group-hover:scale-110 group-hover:rotate-3"
        style={{ 
          backgroundColor: `${color}10`,
          boxShadow: `0 4px 12px -4px ${color}30`
        }}
      >
        <Icon className="w-4 h-4" style={{ color }} />
      </div>
      
      <div className="flex-1">
        <span className="text-sm font-medium block mb-0.5" style={{ color: currentTheme.text }}>
          {children}
        </span>
        {description && (
          <span className="text-xs" style={{ color: `${currentTheme.text}50` }}>
            {description}
          </span>
        )}
      </div>
      
      <div 
        className="w-6 h-6 rounded-full flex items-center justify-center transition-all duration-300 group-hover:bg-white group-hover:scale-110"
        style={{ backgroundColor: `${color}08` }}
      >
        <FaArrowRight className="text-[10px] transition-transform duration-300 group-hover:translate-x-0.5" style={{ color }} />
      </div>
    </Link>
  );

  const ActivityItem: React.FC<ActivityItemProps> = ({ activity }) => {
    const getIcon = () => {
      if (activity.type === 'verification') {
        if (activity.userType === 'influencer') return FaUserCircle;
        if (activity.userType === 'organization') return FaBuilding;
        return activity.status === 'pending' ? FaClock : FaCheckCircle;
      }
      if (activity.type === 'campaign') return FaBullhorn;
      return FaClipboardCheck;
    };

    const getIconColor = (): string => {
      if (activity.status === 'pending') return currentTheme.warning;
      if (activity.status === 'approved') return currentTheme.success;
      if (activity.status === 'rejected') return currentTheme.error;
      return currentTheme.primary;
    };

    const Icon = getIcon();
    const iconColor = getIconColor();

    // Get the review link for activity
    const getReviewLink = () => {
      if (activity.type === 'verification' && activity.userId) {
        if (activity.userType === 'influencer') {
          return `/dashboard/viewdocs/influencer/${activity.userId}`;
        } else {
          return `/dashboard/viewdocs/${activity.userId}`;
        }
      }
      return '#';
    };

    const reviewLink = getReviewLink();

    return (
      <Link
        to={reviewLink}
        className="block"
      >
        <div 
          className="flex items-start gap-3 p-3 rounded-xl transition-all duration-200 hover:shadow-sm cursor-pointer"
          style={{ 
            backgroundColor: `${currentTheme.surface}98`,
            border: `1px solid ${currentTheme.border}`,
          }}
        >
          <div 
            className="p-2 rounded-lg shrink-0"
            style={{ backgroundColor: `${iconColor}08` }}
          >
            <Icon className="w-3.5 h-3.5" style={{ color: iconColor }} />
          </div>
          
          <div className="flex-1 min-w-0">
            <p className="text-sm font-medium truncate mb-1" style={{ color: currentTheme.text }}>
              {activity.message}
            </p>
            <div className="flex items-center gap-3">
              <div className="flex items-center gap-1.5">
                <FaClock className="w-3 h-3" style={{ color: `${currentTheme.text}30` }} />
                <span className="text-xs" style={{ color: `${currentTheme.text}50` }}>
                  {new Date(activity.time).toLocaleDateString()}
                </span>
              </div>
              {activity.userType && (
                <span 
                  className="text-[10px] px-2 py-0.5 rounded-full"
                  style={{ 
                    backgroundColor: activity.userType === 'influencer' ? `${currentTheme.primary}08` : `${currentTheme.secondary}08`,
                    color: activity.userType === 'influencer' ? currentTheme.primary : currentTheme.secondary,
                    border: `1px solid ${activity.userType === 'influencer' ? currentTheme.primary : currentTheme.secondary}15`
                  }}
                >
                  {activity.userType === 'influencer' ? 'Influencer' : 'Organization'}
                </span>
              )}
            </div>
          </div>
        </div>
      </Link>
    );
  };

  // Helper function to get user info with correct IDs
  const getUserInfo = (request: VerificationRequest) => {
    // Check if it's an influencer (has influencer_name or type is 'influencer')
    if (request.influencer_name || request.type === 'influencer') {
      return {
        type: 'influencer',
        name: request.influencer_name || 'Influencer',
        icon: FaUserCircle,
        color: currentTheme.primary,
        userId: request.influencer_id || request.id // Use influencer_id if available, fallback to request id
      };
    } 
    // Check if it's an organization (has organization_name)
    else if (request.organization_name) {
      return {
        type: 'organization',
        name: request.organization_name,
        icon: FaBuilding,
        color: currentTheme.secondary,
        userId: request.organization_id || request.id // Use organization_id if available, fallback to request id
      };
    }
    // Unknown type
    return {
      type: 'unknown',
      name: '-',
      icon: FaUserCircle,
      color: currentTheme.text,
      userId: request.id
    };
  };

  // Function to get the review link based on user type using correct IDs
  const getReviewLink = (request: VerificationRequest) => {
    // For influencers
    if (request.influencer_name || request.type === 'influencer') {
      const userId = request.influencer_id || request.id;
      return `/dashboard/viewdocs/influencer/${userId}`;
    } 
    // For organizations
    else {
      const userId = request.organization_id || request.id;
      return `/dashboard/viewdocs/${userId}`;
    }
  };

  return (
    <div className="min-h-screen p-8 space-y-8" style={{ backgroundColor: currentTheme.background }}>
      {/* Header with actions */}
      <div className="flex flex-col md:flex-row md:items-center justify-between gap-4">
        <div>
          <h1 className="text-3xl font-bold mb-2 tracking-tight" style={{ color: currentTheme.text }}>
            Dashboard Overview
          </h1>
          <p className="text-sm" style={{ color: `${currentTheme.text}60` }}>
            Welcome back! Here's what's happening with your platform.
          </p>
        </div>
        
        <div className="flex items-center gap-3">
          {/* Time range selector */}
          <div 
            className="flex items-center gap-1 p-1 rounded-lg"
            style={{ backgroundColor: `${currentTheme.text}05`, border: `1px solid ${currentTheme.border}` }}
          >
            {(['week', 'month', 'year'] as const).map((range) => (
              <button
                key={range}
                onClick={() => setTimeRange(range)}
                className={`px-3 py-1.5 text-xs font-medium rounded-md transition-all duration-200 ${
                  timeRange === range ? 'shadow-sm' : ''
                }`}
                style={{ 
                  backgroundColor: timeRange === range ? currentTheme.surface : 'transparent',
                  color: timeRange === range ? currentTheme.primary : `${currentTheme.text}60`,
                  border: timeRange === range ? `1px solid ${currentTheme.primary}20` : 'none'
                }}
              >
                {range.charAt(0).toUpperCase() + range.slice(1)}
              </button>
            ))}
          </div>

          {/* Action buttons */}
          <button 
            className="p-2.5 rounded-lg transition-all duration-200 hover:shadow-sm"
            style={{ backgroundColor: `${currentTheme.text}05`, border: `1px solid ${currentTheme.border}` }}
          >
            <FaFilter className="w-4 h-4" style={{ color: `${currentTheme.text}60` }} />
          </button>
          <button 
            className="p-2.5 rounded-lg transition-all duration-200 hover:shadow-sm"
            style={{ backgroundColor: `${currentTheme.text}05`, border: `1px solid ${currentTheme.border}` }}
          >
            <FaDownload className="w-4 h-4" style={{ color: `${currentTheme.text}60` }} />
          </button>
        </div>
      </div>

      {/* Stats Grid */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
        <StatCard
          icon={FaClipboardCheck}
          title="Pending Verifications"
          value={stats.verifications}
          color={currentTheme.primary}
          subtitle={`${totalVerifications} total requests`}
          trend={12}
        />
        <StatCard
          icon={FaUsers}
          title="Verified Users"
          value={stats.users}
          color={currentTheme.success}
          subtitle="Active accounts"
          trend={8}
        />
        <StatCard
          icon={FaBuilding}
          title="Total Brands"
          value={stats.brands}
          color={currentTheme.secondary}
          subtitle="Registered brands"
          trend={5}
        />
        <StatCard
          icon={FaBullhorn}
          title="Active Campaigns"
          value={stats.campaings}
          color={currentTheme.warning}
          subtitle="Running campaigns"
          trend={-2}
        />
      </div>

      {/* Charts Section */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* Bar Chart */}
        <div className="lg:col-span-2 rounded-2xl p-6 transition-all duration-300 hover:shadow-lg" 
             style={{ backgroundColor: currentTheme.surface, border: `1px solid ${currentTheme.border}` }}>
          <div className="flex items-center justify-between mb-6">
            <div>
              <h3 className="text-base font-semibold mb-1" style={{ color: currentTheme.text }}>
                Activity Overview
              </h3>
              <p className="text-xs" style={{ color: `${currentTheme.text}50` }}>
                Distribution across platform metrics
              </p>
            </div>
            <span className="text-xs px-3 py-1.5 rounded-full" style={{ backgroundColor: `${currentTheme.primary}08`, color: currentTheme.primary, border: `1px solid ${currentTheme.primary}15` }}>
              Last 30 days
            </span>
          </div>
          
          <div className="h-80">
            <ResponsiveContainer width="100%" height="100%">
              <BarChart data={barChartData} margin={{ top: 20, right: 30, left: 0, bottom: 5 }}>
                <CartesianGrid strokeDasharray="3 3" stroke={`${currentTheme.text}08`} vertical={false} />
                <XAxis dataKey="name" stroke={`${currentTheme.text}30`} tick={{ fontSize: 12 }} axisLine={false} tickLine={false} />
                <YAxis stroke={`${currentTheme.text}30`} tick={{ fontSize: 12 }} axisLine={false} tickLine={false} />
                <Tooltip
                  contentStyle={{
                    backgroundColor: currentTheme.surface,
                    border: `1px solid ${currentTheme.border}`,
                    borderRadius: '12px',
                    fontSize: '12px',
                    padding: '12px',
                    boxShadow: '0 8px 30px rgba(0,0,0,0.08)'
                  }}
                />
                <Bar dataKey="value" radius={[6, 6, 0, 0]} barSize={40}>
                  {barChartData.map((entry, index) => (
                    <Cell key={`bar-${index}`} fill={COLORS[index % COLORS.length]} />
                  ))}
                </Bar>
              </BarChart>
            </ResponsiveContainer>
          </div>
        </div>

        {/* Pie Chart */}
        <div className="rounded-2xl p-6 transition-all duration-300 hover:shadow-lg" 
             style={{ backgroundColor: currentTheme.surface, border: `1px solid ${currentTheme.border}` }}>
          <div className="mb-6">
            <h3 className="text-base font-semibold mb-1" style={{ color: currentTheme.text }}>
              Distribution
            </h3>
            <p className="text-xs" style={{ color: `${currentTheme.text}50` }}>
              Platform metrics breakdown
            </p>
          </div>
          
          <div className="h-80">
            <ResponsiveContainer width="100%" height="100%">
              <PieChart>
                <Pie
                  data={pieChartData}
                  cx="50%"
                  cy="50%"
                  innerRadius={60}
                  outerRadius={90}
                  paddingAngle={3}
                  dataKey="value"
                >
                  {pieChartData.map((entry, index) => (
                    <Cell key={`pie-${index}`} fill={COLORS[index % COLORS.length]} />
                  ))}
                </Pie>
                <Tooltip
                  contentStyle={{
                    backgroundColor: currentTheme.surface,
                    border: `1px solid ${currentTheme.border}`,
                    borderRadius: '12px',
                    fontSize: '12px',
                    padding: '12px',
                    boxShadow: '0 8px 30px rgba(0,0,0,0.08)'
                  }}
                />
              </PieChart>
            </ResponsiveContainer>
          </div>
        </div>
      </div>

      {/* Quick Actions */}
      <div className="rounded-2xl p-6 transition-all duration-300 hover:shadow-lg" 
           style={{ backgroundColor: currentTheme.surface, border: `1px solid ${currentTheme.border}` }}>
        <div className="mb-6">
          <h3 className="text-base font-semibold mb-1" style={{ color: currentTheme.text }}>
            Quick Actions
          </h3>
          <p className="text-xs" style={{ color: `${currentTheme.text}50` }}>
            Frequently used operations
          </p>
        </div>
        
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
          <QuickActionButton 
            to="/dashboard/verification/requests" 
            icon={FaClipboardCheck} 
            color={currentTheme.primary}
            description="Review pending requests"
          >
            Verifications
          </QuickActionButton>
          <QuickActionButton 
            to="/dashboard/brands" 
            icon={FaBuilding} 
            color={currentTheme.secondary}
            description="Manage brand profiles"
          >
            Brands
          </QuickActionButton>
          <QuickActionButton 
            to="/dashboard/campaings" 
            icon={FaBullhorn} 
            color={currentTheme.warning}
            description="Create & manage campaigns"
          >
            Campaigns
          </QuickActionButton>
          <QuickActionButton 
            to="/dashboard/users/all" 
            icon={FaUsers} 
            color={currentTheme.success}
            description="View user activity"
          >
            Users
          </QuickActionButton>
        </div>
      </div>

      {/* Recent Activities and Pending Verifications */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* Recent Activities */}
        <div className="rounded-2xl p-6 transition-all duration-300 hover:shadow-lg" 
             style={{ backgroundColor: currentTheme.surface, border: `1px solid ${currentTheme.border}` }}>
          <div className="mb-6">
            <h3 className="text-base font-semibold mb-1" style={{ color: currentTheme.text }}>
              Recent Activities
            </h3>
            <p className="text-xs" style={{ color: `${currentTheme.text}50` }}>
              Latest platform updates
            </p>
          </div>
          
          <div className="space-y-3">
            {recentActivities.length > 0 ? (
              recentActivities.map((activity, index) => (
                <ActivityItem key={index} activity={activity} />
              ))
            ) : (
              <div className="text-center py-12">
                <div className="w-16 h-16 mx-auto mb-4 rounded-full flex items-center justify-center" 
                     style={{ backgroundColor: `${currentTheme.success}08` }}>
                  <FaCheckCircle className="w-8 h-8" style={{ color: `${currentTheme.success}30` }} />
                </div>
                <p className="text-sm font-medium mb-1" style={{ color: currentTheme.text }}>
                  No recent activities
                </p>
                <p className="text-xs" style={{ color: `${currentTheme.text}50` }}>
                  Activities will appear here
                </p>
              </div>
            )}
          </div>
        </div>

        {/* Pending Verifications Table */}
        <div className="lg:col-span-2 rounded-2xl p-6 transition-all duration-300 hover:shadow-lg" 
             style={{ backgroundColor: currentTheme.surface, border: `1px solid ${currentTheme.border}` }}>
          <div className="flex items-center justify-between mb-6">
            <div>
              <h3 className="text-base font-semibold mb-1" style={{ color: currentTheme.text }}>
                Pending Verifications
              </h3>
              <p className="text-xs" style={{ color: `${currentTheme.text}50` }}>
                Requests awaiting your review
              </p>
            </div>
            
            {requests.length > 0 && (
              <div className="flex items-center gap-2">
                <span className="text-xs px-3 py-1.5 rounded-full font-medium" 
                      style={{ backgroundColor: `${currentTheme.warning}08`, color: currentTheme.warning, border: `1px solid ${currentTheme.warning}15` }}>
                  {requests.length} pending
                </span>
              </div>
            )}
          </div>
          
          <div className="overflow-x-auto">
            {requests.length === 0 ? (
              <div className="text-center py-16">
                <div className="w-20 h-20 mx-auto mb-4 rounded-full flex items-center justify-center" 
                     style={{ backgroundColor: `${currentTheme.success}08` }}>
                  <FaCheckCircle className="w-10 h-10" style={{ color: `${currentTheme.success}30` }} />
                </div>
                <p className="text-base font-semibold mb-2" style={{ color: currentTheme.text }}>
                  All clear!
                </p>
                <p className="text-sm" style={{ color: `${currentTheme.text}50` }}>
                  No pending verification requests
                </p>
              </div>
            ) : (
              <table className="w-full">
                <thead>
                  <tr style={{ borderBottom: `1px solid ${currentTheme.border}` }}>
                    <th className="pb-4 text-left text-xs font-medium" style={{ color: `${currentTheme.text}50` }}>Type</th>
                    <th className="pb-4 text-left text-xs font-medium" style={{ color: `${currentTheme.text}50` }}>Name</th>
                    <th className="pb-4 text-left text-xs font-medium" style={{ color: `${currentTheme.text}50` }}>Requested</th>
                    <th className="pb-4 text-left text-xs font-medium" style={{ color: `${currentTheme.text}50` }}>Status</th>
                    <th className="pb-4 text-left text-xs font-medium" style={{ color: `${currentTheme.text}50` }}></th>
                  </tr>
                </thead>
                <tbody>
                  {requests.slice(0, 5).map((item, index) => {
                    const userInfo = getUserInfo(item);
                    const TypeIcon = userInfo.icon;
                    const reviewLink = getReviewLink(item);
                    
                    return (
                      <tr key={index} className="group" style={{ borderBottom: index < requests.slice(0, 5).length - 1 ? `1px solid ${currentTheme.border}` : 'none' }}>
                        <td className="py-4">
                          <div className="flex items-center gap-2">
                            <div 
                              className="p-1.5 rounded-lg transition-transform duration-200 group-hover:scale-110"
                              style={{ backgroundColor: `${userInfo.color}08` }}
                            >
                              <TypeIcon className="w-4 h-4" style={{ color: userInfo.color }} />
                            </div>
                            <span className="text-xs font-medium" style={{ color: `${currentTheme.text}80` }}>
                              {userInfo.type === 'influencer' ? 'Influencer' : 'Organization'}
                            </span>
                          </div>
                        </td>
                        <td className="py-4">
                          <span className="text-sm font-medium" style={{ color: currentTheme.text }}>
                            {userInfo.name}
                          </span>
                        </td>
                        <td className="py-4">
                          <div className="flex items-center gap-1.5">
                            <FaCalendarAlt className="w-3 h-3" style={{ color: `${currentTheme.text}30` }} />
                            <span className="text-xs" style={{ color: `${currentTheme.text}60` }}>
                              {item.requested_at
                                ? new Date(item.requested_at).toLocaleDateString()
                                : "N/A"}
                            </span>
                          </div>
                        </td>
                        <td className="py-4">
                          <span 
                            className="text-xs px-3 py-1.5 rounded-full inline-flex items-center gap-1.5 font-medium"
                            style={{ 
                              backgroundColor: `${currentTheme.warning}08`,
                              color: currentTheme.warning,
                              border: `1px solid ${currentTheme.warning}15`
                            }}
                          >
                            <FaExclamationTriangle className="w-3 h-3" />
                            Pending
                          </span>
                        </td>
                        <td className="py-4">
                          <Link
                            to={reviewLink}
                            className="inline-flex items-center gap-2 px-4 py-2 rounded-lg transition-all duration-200 hover:shadow-sm text-xs font-medium group"
                            style={{ 
                              backgroundColor: `${currentTheme.primary}05`,
                              color: currentTheme.primary,
                              border: `1px solid ${currentTheme.primary}12`
                            }}
                          >
                            <span>Review</span>
                            <FaArrowRight className="w-3 h-3 transition-transform duration-200 group-hover:translate-x-0.5" />
                          </Link>
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            )}
          </div>
          
          {requests.length > 5 && (
            <div className="mt-6 text-center">
              <Link
                to="/dashboard/verification/requests"
                className="inline-flex items-center gap-2 px-6 py-3 rounded-xl transition-all duration-200 hover:shadow-md text-sm font-medium group"
                style={{ 
                  backgroundColor: `${currentTheme.primary}04`,
                  color: currentTheme.primary,
                  border: `1px solid ${currentTheme.primary}12`
                }}
              >
                View all {requests.length} requests
                <FaArrowRight className="w-3 h-3 transition-transform duration-200 group-hover:translate-x-1" />
              </Link>
            </div>
          )}
        </div>
      </div>

      {/* Trend Chart */}
      <div className="rounded-2xl p-6 transition-all duration-300 hover:shadow-lg" 
           style={{ backgroundColor: currentTheme.surface, border: `1px solid ${currentTheme.border}` }}>
        <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4 mb-6">
          <div>
            <h3 className="text-base font-semibold mb-1" style={{ color: currentTheme.text }}>
              Growth Trends
            </h3>
            <p className="text-xs" style={{ color: `${currentTheme.text}50` }}>
              Monthly performance metrics
            </p>
          </div>
          
          <div className="flex gap-4">
            {['Users', 'Campaigns', 'Brands'].map((item, index) => (
              <div key={item} className="flex items-center gap-2">
                <div className="w-2.5 h-2.5 rounded-full" style={{ backgroundColor: COLORS[index], boxShadow: `0 0 0 2px ${COLORS[index]}20` }} />
                <span className="text-xs font-medium" style={{ color: `${currentTheme.text}70` }}>{item}</span>
              </div>
            ))}
          </div>
        </div>
        
        <div className="h-80">
          <ResponsiveContainer width="100%" height="100%">
            <AreaChart data={trendData} margin={{ top: 10, right: 30, left: 0, bottom: 0 }}>
              <defs>
                {COLORS.map((color, index) => (
                  <linearGradient key={`gradient-${index}`} id={`colorGradient${index}`} x1="0" y1="0" x2="0" y2="1">
                    <stop offset="5%" stopColor={color} stopOpacity={0.15}/>
                    <stop offset="95%" stopColor={color} stopOpacity={0}/>
                  </linearGradient>
                ))}
              </defs>
              <CartesianGrid strokeDasharray="3 3" stroke={`${currentTheme.text}08`} vertical={false} />
              <XAxis dataKey="month" stroke={`${currentTheme.text}30`} tick={{ fontSize: 12 }} axisLine={false} tickLine={false} />
              <YAxis stroke={`${currentTheme.text}30`} tick={{ fontSize: 12 }} axisLine={false} tickLine={false} />
              <Tooltip
                contentStyle={{
                  backgroundColor: currentTheme.surface,
                  border: `1px solid ${currentTheme.border}`,
                  borderRadius: '12px',
                  fontSize: '12px',
                  padding: '12px',
                  boxShadow: '0 8px 30px rgba(0,0,0,0.08)'
                }}
              />
              <Area type="monotone" dataKey="users" stroke={COLORS[0]} strokeWidth={2.5} fill={`url(#colorGradient0)`} />
              <Area type="monotone" dataKey="campaigns" stroke={COLORS[1]} strokeWidth={2.5} fill={`url(#colorGradient1)`} />
              <Area type="monotone" dataKey="brands" stroke={COLORS[2]} strokeWidth={2.5} fill={`url(#colorGradient2)`} />
            </AreaChart>
          </ResponsiveContainer>
        </div>
      </div>
    </div>
  );
};

export default AdminDashboard;